import React, { useId, useState, useEffect, useRef } from 'react';
import {
  buildContext,
  buildSearchEngine,
  buildStandaloneSearchBox,
  ContextPayload
} from '@coveo/headless';
import { rovingIndex } from 'roving-ux';
import { createDataLayerString } from '../../helpers/helpers';
import { ExtendedWindowType } from '../engine';
import { secureStorage } from '../../helpers/global-storage';
import { PreprocessRequest } from '@coveo/headless/dist/definitions/api/preprocess-request';

const wrapper = document.getElementsByClassName('coveo-predictive-search')[0];

const { dataset } = wrapper as HTMLElement;

const coveoData = JSON.parse(dataset.coveo as string);
const optionsData = JSON.parse(dataset.options as string);

const preprocessRequestFilterData = dataset.preprocessRequestFilterData
  ? JSON.parse(dataset.preprocessRequestFilterData as string)
  : undefined;

const preprocessRequest: PreprocessRequest = (request, clientOrigin) => {
  if (clientOrigin === 'searchApiFetch' && request.body) {
    if (preprocessRequestFilterData) {
      const newBody = JSON.parse(request.body.toString());
      newBody.mlParameters = preprocessRequestFilterData.mlParameters;
      request.body = JSON.stringify(newBody);
    }
  }

  // Return the modified request
  return request;
};

const config = {
  organizationId: coveoData.OrganizationId,
  accessToken: coveoData.ApiKey,
  search: {
    searchHub: coveoData.SearchHubPipeline,
    pipeline: coveoData.SearchHubPipeline
  },
  preprocessRequest
};

const engine = buildSearchEngine({
  configuration: config
});

const controller = buildStandaloneSearchBox(engine, {
  options: {
    ...optionsData,
    highlightOptions: {
      exactMatchDelimiters: {
        open: '<strong>',
        close: '</strong>'
      }
    }
  }
});

type PredictiveSearchProps = {
  placeholder: string;
  containerClass?: string;
  fieldClass?: string;
  submitLabel: string;
  storageKey: string;
  viewAllLabel: string;
  layer?: string;
  clickDataLayer?: string;
  viewDataLayer?: string;
  clearLabel?: string;
  withSubmitIcon?: boolean;
  numberOfCharactersToShow?: number;
  coveoContext?: string;
};

function PredictiveSearch({
  placeholder,
  fieldClass,
  submitLabel,
  containerClass,
  storageKey,
  viewAllLabel,
  layer,
  clickDataLayer,
  viewDataLayer,
  clearLabel,
  withSubmitIcon = false,
  numberOfCharactersToShow = 0,
  coveoContext
}: PredictiveSearchProps) {
  const id = useId();
  const [state, setState] = useState(controller.state);
  const [value, setValue] = useState(controller.state.value);
  const [showingSuggestions, setShowingSuggestions] = useState(false);
  const [focus, setFocus] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const clickDataLayerObj = clickDataLayer
    ? JSON.parse(clickDataLayer)
    : undefined;
  const viewDataLayerObj = viewDataLayer
    ? JSON.parse(viewDataLayer)
    : undefined;

  const isEnterKey = (e: React.KeyboardEvent<HTMLFormElement>) =>
    e.key === 'Enter';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (input.current === document.activeElement) {
      if (isEnterKey(e)) {
        e.preventDefault();
        controller.updateText(value);
        controller.submit();
        setFocus(false);
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (listRef.current) {
          if (e.key === 'ArrowDown') {
            const firstElement = listRef.current.querySelector('li');
            if (firstElement) {
              firstElement.querySelector('button')?.focus();
              setFocus(true);
            }
          } else {
            const lastElement = listRef.current.querySelector('li:last-child');
            if (lastElement) {
              (lastElement as HTMLLIElement).querySelector('button')?.focus();
              setFocus(true);
            }
          }
        }
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        input.current?.blur();
        setFocus(false);
      }
    } else {
      if (e.key === 'Escape') {
        e.preventDefault();
        input.current?.focus();
        setFocus(false);
      }
      if (
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        e.currentTarget.querySelector('.field--search')
      ) {
        e.preventDefault();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    controller.updateText(e.target.value);
    setValue(e.target.value);
    setFocus(true);
  };

  const handleSuggestionClick = (value: string) => {
    setValue(value);
    controller.selectSuggestion(value);
  };

  const handleMouseDown = (e: Event) => {
    if ((e.target as HTMLElement)?.closest('.predictive-search__inner')) {
      return;
    } else {
      setFocus(false);
    }
  };

  const handleClear = () => {
    setValue('');
    controller.updateText('');
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (
      !(e.relatedTarget as HTMLElement)?.closest('.predictive-search__inner')
    ) {
      setFocus(false);
    }
  };

  const handleSubmit = () => {
    controller.updateText(value);
    controller.submit();
  };

  useEffect(() => {
    controller.subscribe(() => setState(controller.state));

    if (coveoContext) {
      const context = buildContext(engine);
      context?.set(JSON.parse(coveoContext) as ContextPayload);
    }
  }, []);

  useEffect(() => {
    if (listRef.current) {
      rovingIndex({
        element: listRef.current,
        target: '.predictive-search__item'
      });

      setShowingSuggestions(true);
    } else {
      setShowingSuggestions(false);
    }
  }, [state.suggestions]);

  useEffect(() => {
    window.addEventListener('mousedown', e => handleMouseDown(e));
    return () => {
      window.removeEventListener('mousedown', e => handleMouseDown(e));
    };
  }, [input.current]);

  useEffect(() => {
    if (showingSuggestions) {
      const global = window as ExtendedWindowType;

      global.dataLayer?.push(viewDataLayerObj);
    }
  }, [showingSuggestions]);

  if (!state) {
    return null;
  }

  if (state.redirectTo) {
    const { redirectTo, value, analytics } = state;
    const data = JSON.stringify({ value, analytics });
    secureStorage.set(storageKey, data);
    window.location.href = redirectTo;
  }

  const getViewAllDataLayer = () => {
    if (clickDataLayerObj) {
      clickDataLayerObj.event_attributes.search_term =
        viewAllLabel.toLowerCase();
    }

    return clickDataLayerObj;
  };

  return (
    <form
      className={`predictive-search ${containerClass}`}
      onSubmit={e => {
        e.preventDefault();
      }}
      onKeyDown={e => handleKeyDown(e)}
    >
      <div className={`field field--required ${fieldClass}`}>
        <label
          className="field__label sr-only"
          htmlFor={`predictive-search-${id}`}
        ></label>
        <div className="predictive-search__inner">
          <input
            value={state.value}
            className="field__input"
            type="text"
            id={`predictive-search-${id}`}
            name={`predictive-search-${id}`}
            placeholder={placeholder}
            required
            onInput={handleInputChange}
            ref={input}
            onFocus={() => setFocus(true)}
            onBlur={e => handleInputBlur(e)}
          />
          {state.suggestions.length > 0 &&
            state.value &&
            state.value.length >= numberOfCharactersToShow &&
            focus && (
              <ul
                className="predictive-search__list"
                ref={listRef}
                onFocus={() => setFocus(true)}
                onBlur={handleInputBlur}
              >
                {state.suggestions.map(suggestion => {
                  const value = suggestion.rawValue;

                  if (clickDataLayerObj) {
                    clickDataLayerObj.event_attributes.search_term =
                      value.toLowerCase();
                  }

                  const dataLayer = clickDataLayerObj;
                  return (
                    <li key={value}>
                      <button
                        className="predictive-search__item"
                        title={value}
                        onClick={() => handleSuggestionClick(value)}
                        dangerouslySetInnerHTML={{
                          __html: suggestion.highlightedValue
                        }}
                        data-layer={
                          dataLayer
                            ? createDataLayerString([dataLayer])
                            : undefined
                        }
                      />
                    </li>
                  );
                })}
                <li>
                  <button
                    className="link link--arrow link--small predictive-search__item"
                    title={viewAllLabel}
                    onClick={() => {
                      controller.updateText(value);
                      controller.submit();
                    }}
                    onFocus={() => {
                      setFocus(true);
                    }}
                    data-layer={
                      clickDataLayerObj
                        ? createDataLayerString([getViewAllDataLayer()])
                        : undefined
                    }
                  >
                    <span>{viewAllLabel}</span>
                  </button>
                </li>
              </ul>
            )}
        </div>

        {state.value.length > 0 && (
          <button
            className="field__input-close"
            title={clearLabel}
            aria-label={clearLabel}
            onClick={handleClear}
          ></button>
        )}
        {withSubmitIcon && (
          <button
            disabled={state.value.length === 0}
            type="submit"
            className="field__input-search"
            title={submitLabel}
            onClick={handleSubmit}
            {...(layer && { 'data-layer': layer })}
          >
            <span className="sr-only">{submitLabel}</span>
          </button>
        )}
      </div>
      {!withSubmitIcon && (
        <button
          disabled={state.value.length === 0}
          type="submit"
          className="btn btn--primary"
          onClick={handleSubmit}
          {...(layer && { 'data-layer': layer })}
        >
          {submitLabel}
        </button>
      )}
    </form>
  );
}

export default PredictiveSearch;
